import PropTypes from 'prop-types';
import { Grid, Card, CardContent, Stack, Box, Typography } from '@mui/material';
import { PeopleAltOutlined, CreditCardOutlined, AccountBalanceWalletOutlined, PendingActionsOutlined } from '@mui/icons-material';
import { formatINR } from '../credit-limits/creditLimitsUtils';

const TransactionHistoryStats = ({ summary = null, totalCount = 0, transactions = [] }) => {
    // ─── Extract Summary Values with Fallback to Table Data ───────────
    let totalEmployees = totalCount || transactions.length || 0;
    let totalUsed = 0;
    let totalRecharge = 0;
    let totalBillPayment = 0;
    let totalRepayment = 0;
    let postRepaymentBills = 0;
    let paidAfterCount = 0;
    let notPaidAfterCount = 0;

    if (summary) {
        totalEmployees = summary.total_employees ?? totalCount ?? transactions.length;
        totalUsed = Number(summary.total_used || 0);
        totalRecharge = Number(summary.total_recharge || 0);
        totalBillPayment = Number(summary.total_billpayment || 0);
        totalRepayment = Number(summary.total_repayment || 0);
        postRepaymentBills = Number(summary.total_used_after_repayment ?? summary.total_billpayment_after_repayment ?? 0);
        paidAfterCount = Number(summary.paid_after_repayment_count || 0);
        notPaidAfterCount = Number(summary.not_paid_after_repayment_count || 0);
    } else {
        // Fallback aggregation over loaded transactions
        transactions.forEach((item) => {
            totalUsed += Number(item.totalused ?? item.amount ?? 0);
            totalRecharge += Number(item.recharge ?? 0);
            totalBillPayment += Number(item.billpayment ?? 0);
            totalRepayment += Number(item.credit_repayment ?? item.paid_amount ?? 0);
            postRepaymentBills += Number(item.total_used_after_repayment ?? item.billpayment_after_repayment ?? 0);
            const status = String(item.bill_paid_after_repayment || '').toUpperCase();
            if (status === 'PAID') paidAfterCount++;
            else if (status === 'NOT PAID') notPaidAfterCount++;
        });
    }

    const repaymentRate = totalUsed > 0 ? Math.min(100, (totalRepayment / totalUsed) * 100).toFixed(1) : 0;

    return (
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {/* 1. Total Employees */}
            <Grid item xs={12} sm={6} md={3}>
                <Card
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                        bgcolor: '#fff',
                        height: '100%',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.08)'
                        }
                    }}
                >
                    <CardContent sx={{ p: 2.5 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                                    Total Employees
                                </Typography>
                                <Typography variant="h4" fontWeight={800} color="text.primary" sx={{ mt: 0.5 }}>
                                    {Number(totalEmployees).toLocaleString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    Enrolled in credit cycle
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2.5,
                                    bgcolor: '#eff6ff',
                                    color: '#1d4ed8'
                                }}
                            >
                                <PeopleAltOutlined />
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            {/* 2. Total Credit Used */}
            <Grid item xs={12} sm={6} md={3}>
                <Card
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                        bgcolor: '#fff',
                        height: '100%',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.08)'
                        }
                    }}
                >
                    <CardContent sx={{ p: 2.5 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                                    Total Used (Cycle)
                                </Typography>
                                <Typography variant="h4" fontWeight={800} color="#0f172a" sx={{ mt: 0.5 }}>
                                    {formatINR(totalUsed)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    Recharge: <strong>{formatINR(totalRecharge)}</strong> • Bills:{' '}
                                    <strong>{formatINR(totalBillPayment)}</strong>
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2.5,
                                    bgcolor: '#f5f3ff',
                                    color: '#6d28d9'
                                }}
                            >
                                <CreditCardOutlined />
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            {/* 3. Total Repayment Settled */}
            <Grid item xs={12} sm={6} md={3}>
                <Card
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                        bgcolor: '#fff',
                        height: '100%',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.08)'
                        }
                    }}
                >
                    <CardContent sx={{ p: 2.5 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                                    Repayment Settled
                                </Typography>
                                <Typography variant="h4" fontWeight={800} color="#15803d" sx={{ mt: 0.5 }}>
                                    {formatINR(totalRepayment)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 600, mt: 0.5, display: 'block' }}>
                                    {totalUsed > 0 ? `${repaymentRate}% settled` : 'Repayment period settlement'}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2.5,
                                    bgcolor: '#f0fdf4',
                                    color: '#15803d'
                                }}
                            >
                                <AccountBalanceWalletOutlined />
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>

            {/* 4. Post-Repayment Bills */}
            <Grid item xs={12} sm={6} md={3}>
                <Card
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                        bgcolor: '#fff',
                        height: '100%',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.08)'
                        }
                    }}
                >
                    <CardContent sx={{ p: 2.5 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                                    Post-Repayment Amount
                                </Typography>
                                <Typography variant="h4" fontWeight={800} color="#b45309" sx={{ mt: 0.5 }}>
                                    {formatINR(postRepaymentBills)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    Paid: <strong>{paidAfterCount}</strong> • Pending: <strong>{notPaidAfterCount}</strong>
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2.5,
                                    bgcolor: '#fffbeb',
                                    color: '#b45309'
                                }}
                            >
                                <PendingActionsOutlined />
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

TransactionHistoryStats.propTypes = {
    summary: PropTypes.shape({
        total_employees: PropTypes.number,
        total_recharge: PropTypes.number,
        total_billpayment: PropTypes.number,
        total_used: PropTypes.number,
        total_repayment: PropTypes.number,
        total_billpayment_after_repayment: PropTypes.number,
        paid_after_repayment_count: PropTypes.number,
        not_paid_after_repayment_count: PropTypes.number
    }),
    totalCount: PropTypes.number,
    transactions: PropTypes.arrayOf(PropTypes.object)
};

export default TransactionHistoryStats;
